/**
 * FATH TIME Logistics brendi (2026-10-25): dizaynerning 3 versiyasi, joyiga qarab (konturlar: fath-time-logo.ts, fayllar: branding/).
 * - BrandIcon: doira -- to'q ko'k doirada qush; yig'ilgan sidebar, favicon ham shu (public/favicon.svg).
 * - BrandLockup: gorizontal -- qush + "FATH TIME" / "LOGISTICS"; tor gorizontal joylar (sidebar, portal, ariza formasi).
 * - BrandLogo: kvadrat -- qush tepada, yozuv ostida; keng joylar (kirish sahifalari, to'q ko'k panelda).
 * Lockup va kvadratda fon yo'q: qush va yozuv -- currentColor (standart text-ink, to'q panelda oq), ko'k chiziq o'zgarmaydi.
 * Login intro ham shu konturlardan (LoginIntro.tsx, HORIZONTAL).
 */
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { CIRCLE, HORIZONTAL, NAVY, SQUARE, STRIPE_STOPS, WHITE, type LogoVersion } from "./fath-time-logo";

/** Ko'k chiziq: bir sahifada bir nechta logo bo'lishi mumkin -- gradient id har nusxada alohida */
function Stripe({ v }: { v: LogoVersion }) {
  const id = `fath-stripe-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <>
      <defs>
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={v.stripeX2} y2={0} gradientTransform={`matrix(${v.stripeMatrix})`}>
          {STRIPE_STOPS.map(([offset, color]) => (
            <stop key={offset} offset={offset} stopColor={color} />
          ))}
        </linearGradient>
      </defs>
      <path d={v.stripe} fill={`url(#${id})`} fillRule="evenodd" />
    </>
  );
}

function Mark({ v, ink }: { v: LogoVersion; ink: string }) {
  return (
    <>
      <path d={v.bird} fill={ink} fillRule="evenodd" />
      <Stripe v={v} />
      {v.title.length > 0 && <path d={v.title.join("")} fill={ink} fillRule="evenodd" />}
      {v.subtitle.length > 0 && <path d={v.subtitle.join("")} fill={ink} />}
    </>
  );
}

/** Fonsiz qism atrofida ozgina bo'sh joy bilan */
function contentBox(v: LogoVersion, pad: number) {
  const [x0, y0, x1, y1] = v.content;
  return { box: `${x0 - pad} ${y0 - pad} ${x1 - x0 + 2 * pad} ${y1 - y0 + 2 * pad}`, w: x1 - x0 + 2 * pad, h: y1 - y0 + 2 * pad };
}

export function BrandIcon({ size = 32, className }: { size?: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <svg viewBox={`0 0 ${CIRCLE.w} ${CIRCLE.h}`} width={size} height={size} role="img" aria-label={t("app")} className={cn("shrink-0", className)}>
      <path d={CIRCLE.bg} fill={NAVY} />
      <Mark v={CIRCLE} ink={WHITE} />
    </svg>
  );
}

const LOCKUP = contentBox(HORIZONTAL, 1);

export function BrandLockup({ height = 36, className }: { height?: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <svg
      viewBox={LOCKUP.box}
      width={(height * LOCKUP.w) / LOCKUP.h}
      height={height}
      role="img"
      aria-label={t("app")}
      className={cn("shrink-0 text-ink", className)}
    >
      <Mark v={HORIZONTAL} ink="currentColor" />
    </svg>
  );
}

const FULL = contentBox(SQUARE, 1);

export function BrandLogo({ onDark = false, className }: { onDark?: boolean; className?: string }) {
  const { t } = useTranslation();
  return (
    <svg viewBox={FULL.box} role="img" aria-label={t("app")} className={cn("h-auto text-ink", onDark && "text-white", className)}>
      <Mark v={SQUARE} ink="currentColor" />
    </svg>
  );
}
